import { DownOutlined, ExportOutlined, HighlightOutlined, KeyOutlined, UserOutlined } from '@ant-design/icons';
import { Flex } from 'antd';
import App from 'antd/lib/app';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Layout from 'antd/lib/layout';
import Typography from 'antd/lib/typography';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useAppContext } from '../../AppContextProvider';
import { AbilityContext } from '../../components/ability/can';
import { AppNavigation } from '../../components/AppNavigation/AppNavigation';
import { FeedbackModal } from '../../components/modals/feedbackModal/FeedbackModal';
import { PageFooter } from '../../components/PageFooter';
import { GlobalCompanySelector } from '../../components/selectors/GlobalCompanySelector/GlobalCompanySelector';
import { initializePendo } from '../../domain/analytics/pendo';
import { useAuth } from '../../domain/auth/useAuth';
import { useCompanyLogo } from '../../domain/company/branding/BrandingProvider';
import { COMPANY_TYPE } from '../../domain/company/interface';
import { fetchUser } from '../../domain/user';
import { IUser } from '../../domain/user/interface';
import { CompanyBrandingModal } from '../../pages/companies/components/CompanyBrandingModal';
import { UserProfile } from '../../pages/userProfile/UserProfile';
import { ApiKeysModal } from '../../pages/api-keys/ApiKeysModal';
import './PrivateLayout.css';
import { KeyRound } from 'lucide-react';

const { Header, Content } = Layout;

enum APP_ACTION {
    FEEDBACK = 'feedback',
    USER_PROFILE = 'user_profile',
    COMPANY_BRANDING = 'company_branding',
    API_KEYS = 'api_keys',
}

export function PrivateLayout() {
    const { notification } = App.useApp();
    const [appAction, setAppAction] = useState<APP_ACTION | ''>('');
    const [user, setUser] = useState<IUser>();
    const logo = useCompanyLogo();

    const auth = useAuth()!;
    const ability = useContext(AbilityContext);

    const { isFiltersSidebarEnabled } = useAppContext();

    const fetchUserAction = useCallback(async () => {
        try {
            const user = await fetchUser(auth.user!.user_id);
            console.info('user loaded');
            setUser(user);
            initializePendo(user);
        } catch (error: any) {
            notification.error({ key: 'fetch-user-error', message: error.message || 'Cannot fetch user!' });
        }
    }, []);

    useEffect(() => {
        fetchUserAction();
    }, []);

    const closeUserProfile = (user?: IUser) => {
        closeAppActionModal();
        if (user) {
            fetchUserAction();
        }
    };

    const closeAppActionModal = () => setAppAction('');
    const showAppActionModal = (action: APP_ACTION) => setAppAction(action);

    const shouldDisplayFooter = !isFiltersSidebarEnabled;

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Header className="header">
                <Link to="/">
                    <img className="app-logo full-logo" src={logo} alt="Enersponse logo" />
                </Link>
                {ability.can('filter', 'Company') && <GlobalCompanySelector />}

                <AppNavigation />

                <UserMenu user={user} showAppActionModal={showAppActionModal} />
            </Header>

            <Content>
                <Flex>
                    <div className="layout-content">
                        <Outlet />

                        {shouldDisplayFooter && <PageFooter />}
                    </div>
                </Flex>
                <Button size="large" className="global-feedback-button" onClick={() => showAppActionModal(APP_ACTION.FEEDBACK)} type="primary">
                    Feedback
                </Button>
            </Content>
            {appAction === APP_ACTION.USER_PROFILE && <UserProfile user={user} onClose={closeUserProfile} />}
            {appAction === APP_ACTION.API_KEYS && <ApiKeysModal onClose={closeAppActionModal} />}
            {appAction === APP_ACTION.FEEDBACK && <FeedbackModal onClose={closeAppActionModal} />}
            {appAction === APP_ACTION.COMPANY_BRANDING && (
                <CompanyBrandingModal company={{ company_id: user?.company_id }} onClose={closeAppActionModal} />
            )}
        </Layout>
    );
}

interface IUserMenuProps {
    user?: IUser;
    showAppActionModal: (action: APP_ACTION) => void;
}

const UserMenu: React.FC<IUserMenuProps> = ({ user, showAppActionModal }) => {
    const auth = useAuth()!;
    const ability = useContext(AbilityContext);

    const signout = () => {
        auth.signout();
    };

    const userMenu = [
        {
            key: 'profile',
            onClick: () => showAppActionModal(APP_ACTION.USER_PROFILE),
            label: (
                <Typography>
                    <UserOutlined /> Profile
                </Typography>
            ),
        },
        {
            key: 'api-keys',
            onClick: () => showAppActionModal(APP_ACTION.API_KEYS),
            label: (
                <Typography>
                    <KeyRound size={14} /> API Keys
                </Typography>
            ),
        },
        ...(ability.can('update:branding', 'Company') && user?.company?.type === COMPANY_TYPE.UTILITY_CUSTOMER
            ? [
                  {
                      key: 'company-branding',
                      onClick: () => showAppActionModal(APP_ACTION.COMPANY_BRANDING),
                      label: (
                          <Typography>
                              <HighlightOutlined /> Company Branding
                          </Typography>
                      ),
                  },
              ]
            : []),
        {
            key: 'signout',
            onClick: signout,
            label: (
                <Typography>
                    <ExportOutlined /> Logout
                </Typography>
            ),
        },
    ];

    return (
        <Dropdown key="user-menu" trigger={['click']} menu={{ items: userMenu }}>
            <div className="user-menu">
                <Avatar size={32} icon={<UserOutlined />} />

                <span className="user-name">{user?.user_fname} </span>

                <DownOutlined className="user-menu-icon" />
            </div>
        </Dropdown>
    );
};
